// Chat-mentions inbox — header button + popover. Lives next to the chat
// button. Surfaces every `chat_mentioned` notification for the caller; click
// opens the chat sheet and selects the mentioned thread via the
// `bp:open-chat-thread` event the ChatThreadList listens for.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AtSign, Check } from '@/lib/icons';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { DASHBOARD_PATHS } from '@/lib/paths';

const POLL_MS = 30_000;

interface ChatMention {
  Id: string;
  Kind: string;
  ChatThreadId: string | null;
  ChatMessageId: string | null;
  ActorUserId: string | null;
  ActorName: string;
  Summary: string;
  ThreadSubject: string | null;
  ProjectId: string | null;
  Read: boolean;
  ReadAt: string | null;
  CreatedAt: string;
}

interface ListResponse { items: ChatMention[]; unreadCount: number; }
interface CountResponse { unreadCount: number; }

export function ChatMentionsInbox({ onOpenChats }: { onOpenChats: () => void }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<ChatMention[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(true);
  const popRef = useRef<HTMLDivElement | null>(null);

  // Background poll of the count only; the heavier list fetch waits for the
  // popover to open (one cheap call per user otherwise).
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await api<CountResponse>(DASHBOARD_PATHS.chatMentionsUnreadCount);
        if (!cancelled) setCount(r.unreadCount);
      } catch { /* ignore */ }
    };
    refresh();
    const t = window.setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(t);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const refreshItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (unreadOnly) params.set('unread', '1');
      params.set('limit', '50');
      const r = await api<ListResponse>(
        `${DASHBOARD_PATHS.chatMentions}?${params}`,
      );
      setItems(r.items);
      setCount(r.unreadCount);
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  useEffect(() => { if (open) void refreshItems(); }, [open, refreshItems]);

  // Outside-click closes.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (popRef.current && !popRef.current.contains(t)) setOpen(false);
    };
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [open]);

  const markOne = async (m: ChatMention) => {
    if (m.Read) return;
    try {
      const r = await api<CountResponse>(DASHBOARD_PATHS.chatMentionsRead, {
        method: 'POST',
        body: { ids: [m.Id] },
      });
      setCount(r.unreadCount);
      setItems(prev => prev.map(x => x.Id === m.Id ? { ...x, Read: true } : x));
    } catch { /* ignore */ }
  };

  const markAll = async () => {
    try {
      const r = await api<CountResponse>(DASHBOARD_PATHS.chatMentionsRead, {
        method: 'POST',
        body: {},
      });
      setCount(r.unreadCount);
      setItems(prev => prev.map(x => ({ ...x, Read: true })));
    } catch { /* ignore */ }
  };

  const onClick = async (m: ChatMention) => {
    if (!m.Read) { void markOne(m); }
    setOpen(false);
    // Open the chat sheet, then deep-link the thread via the event the
    // ChatThreadList instance listens for.
    onOpenChats();
    if (m.ChatThreadId) {
      // Fire next tick so the sheet has mounted ChatThreadList.
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('bp:open-chat-thread', {
          detail: { threadId: m.ChatThreadId },
        }));
      }, 0);
    }
  };

  const grouped = useMemo(() => {
    const groups: Array<{ label: string; items: ChatMention[] }> = [];
    for (const item of items) {
      const label = dayLabel(item.CreatedAt);
      const cur = groups[groups.length - 1];
      if (cur?.label === label) cur.items.push(item);
      else groups.push({ label, items: [item] });
    }
    return groups;
  }, [items]);

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="relative inline-flex h-8 items-center justify-center rounded-md px-2 text-muted-foreground hover:text-foreground hover:bg-accent"
        title={count > 0 ? `Chat mentions · ${count} unread` : 'Chat mentions'}
        aria-label="Chat mentions inbox"
      >
        <AtSign className="size-4" />
        {count > 0 && (
          <span
            aria-label={`${count} unread`}
            className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-mono tabular-nums text-primary-foreground"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-[28rem] max-w-[calc(100vw-1rem)] max-h-[70vh] bg-popover border rounded-md shadow-lg z-50 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setUnreadOnly(true)}
                className={cn(
                  'rounded px-2 py-0.5 text-xs',
                  unreadOnly
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >Unread</button>
              <button
                type="button"
                onClick={() => setUnreadOnly(false)}
                className={cn(
                  'rounded px-2 py-0.5 text-xs',
                  !unreadOnly
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >All</button>
            </div>
            {count > 0 && (
              <button
                onClick={markAll}
                className="text-xs text-muted-foreground hover:text-foreground"
              >Mark all read</button>
            )}
          </div>
          <div className="overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground">Loading…</div>
            )}
            {items.length === 0 && !loading && (
              <div className="p-4 text-sm text-muted-foreground">No chat mentions.</div>
            )}
            {grouped.map(group => (
              <section key={group.label}>
                <div className="sticky top-0 z-10 border-b bg-popover px-3 py-1 text-[10px] font-medium uppercase text-muted-foreground">
                  {group.label}
                </div>
                {group.items.map(m => (
                  <button
                    key={m.Id}
                    onClick={() => onClick(m)}
                    className={cn(
                      'group flex w-full items-start gap-2 border-b px-3 py-2 text-left hover:bg-accent/50',
                      !m.Read && 'bg-accent/30',
                    )}
                  >
                    <span className={cn(
                      'mt-1.5 size-1.5 shrink-0 rounded-full',
                      m.Read ? 'bg-muted-foreground/30' : 'bg-primary',
                    )} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] text-muted-foreground truncate">
                        {m.ThreadSubject ? m.ThreadSubject : 'Chat'} · {compactTime(m.CreatedAt)}
                      </span>
                      <span className="block text-sm line-clamp-2">{m.Summary}</span>
                    </span>
                    {!m.Read && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); void markOne(m); }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return;
                          e.preventDefault(); e.stopPropagation();
                          void markOne(m);
                        }}
                        className="mt-1 rounded p-1 text-muted-foreground opacity-0 hover:bg-accent hover:text-foreground group-hover:opacity-100 focus:opacity-100"
                        title="Mark read"
                      >
                        <Check className="size-3.5" />
                      </span>
                    )}
                  </button>
                ))}
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function dayLabel(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Older';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return 'Older';
}

function compactTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
